import React, { FC, useEffect, useState } from 'react';
import Select, { components } from "react-select";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { DirectionE, IconColorE, SortingDirectionsE } from "../../models/enums";
import useApp from "../../utils/hooks/useApp";
import useQueryParams from "../../utils/hooks/useQueryParams";
import { Span } from "../Text/Text";
import Icon from "../Icon/Icon";
import { customSortSelectStyles } from "./customSelectStyles";
import { getSortingTypeWithDirection } from "./helpers/helpers";
import { SelectOptionT } from "../../models/models";
import { clss } from '../../utils/helpers/styleHelpers';

import styles from './orderBy.module.scss';

/**
 * Adds query params to route base on chosen values
 * */

// values for the sorting query param
const orderByOptions: SelectOptionT[] = [
    { value: 'num', label: 'Delivery number' },
    { value: 'name', label: 'Name' },
    { value: 'cdate', label: 'Creation date' },
    { value: 'bdate', label: 'Broadcasting date' },
];

type OrderByPropTypes = {
    options?: SelectOptionT[];
};

const OrderBy: FC<OrderByPropTypes> = (props) => {
    const { options } = props;
    // get access to the routing and query params
    const { searchParams, addQueryParam } = useQueryParams();
    const [sortingDirection, setSortingDirection] = useState(SortingDirectionsE.DESC)
    const [sorting, setSorting] = useState(orderByOptions[0]);

    // get app global data
    const { t } = useTranslate();
    const { direction } = useApp();
    const lang = direction === DirectionE.RTL ? 'he' : 'en';

    // set default value for the select from the query param
    useEffect(() => {
        // get value from the query param
        const sortingQueryParam = searchParams.get('sorting');

        // set default direction and sort type values if they exist
        if (sortingQueryParam) {
            const isDescDirection = sortingQueryParam.includes('_desc');

            // get query param without direction postfix
            const sortingQueryParamWithoutDirection = isDescDirection
                ? sortingQueryParam.slice(0, -5)
                : sortingQueryParam;
            const chosenSortingOptionIndex = orderByOptions
                .findIndex(({ value }) => value === sortingQueryParamWithoutDirection);

            setSorting(options ? options[chosenSortingOptionIndex] : orderByOptions[chosenSortingOptionIndex]);
            setSortingDirection(isDescDirection ? SortingDirectionsE.DESC : SortingDirectionsE.ASC);
        } else {
            // add new query param
            addQueryParam(
                { sorting: getSortingTypeWithDirection(orderByOptions[0].value, sortingDirection) },
                { replace: true }
            );
            setSorting(options ? options[0] : orderByOptions[0]);
        }
    }, []);

    // custom dropdown arrow component
    const DropdownIndicator = (props: any) => {
        return (
            components.DropdownIndicator && (
                <components.DropdownIndicator {...props}>
                    <Icon name='selectArrow' color={IconColorE.GRAY} />
                </components.DropdownIndicator>
            )
        );
    };

    const handleChangeSort = (value: any) => {
        setSorting(value as any);
        addQueryParam({ sorting: getSortingTypeWithDirection(value.value, sortingDirection) });
    };

    // change sorting direction
    const handleToggleSortDirection = () => {
        setSortingDirection(prev => {
            // get new sorting direction
            const newDirection = prev === SortingDirectionsE.DESC ? SortingDirectionsE.ASC : SortingDirectionsE.DESC;

            // replace direction within the sorting query param
            addQueryParam({ sorting: getSortingTypeWithDirection(sorting.value, newDirection) });

            return newDirection;
        });
    };

    return (
        <div className={styles.wrapper}>
            <Span>Order by:</Span>
            {/*@ts-ignore*/}
            <Select
                options={options || orderByOptions}
                styles={customSortSelectStyles(lang)}
                value={sorting}
                onChange={handleChangeSort}
                isSearchable={false}
                getOptionLabel={({ label }: SelectOptionT) => t(label)}
                isRtl={direction === DirectionE.RTL}
                components={{ DropdownIndicator }}
                menuPlacement='top'
            />
            <div className={styles.iconWrapper}>
                <Icon
                    name='sortDirection'
                    className={clss([sortingDirection === SortingDirectionsE.DESC ? styles.sortIconDesc : styles.sortIconAsc, styles.sortIcon])}
                    title={sortingDirection === SortingDirectionsE.DESC ? 'Sort by descend' : 'Sort by ascend'}
                    color={IconColorE.BLUE}
                    onClick={handleToggleSortDirection}
                />
            </div>
        </div>
    );
};

export default OrderBy;